<template>
	<ViewFeedItem class="view-feed-tvplayinglive">
		<template v-slot:header>
			<ViewLayoutModuleHeader title="Live Now">
				<RouterLink v-bind:to="{ name: 'PageTV' }">
					<strong>Watch TV</strong>
				</RouterLink>
			</ViewLayoutModuleHeader>
		</template>

		<template v-slot:body>
			<section v-if="liveCollection.length">
				<div class="carousel">
					<div class="track" ref="track">
						<div class="slide" v-for="(model, index) in visibleModels" v-bind:key="model.cid">
							<ChalkyTvVideoTheater
								v-bind:allowControl="false"
								v-bind:mute="true"
								v-bind:play="index === activeIndex"
								v-bind:scheduleModel="model"
								v-on:player:ended="Handle_OnPlayerEnded"
								v-on:player:error="Handle_OnPlayerError"
								v-on:player:ready="Handle_OnPlayerReady"
							/>
						</div>
					</div>
				</div>

				<div class="button-group padded">
					<button class="button-secondary size-small" v-on:click="Handle_OnClickPrevious">Previous</button>
					<button class="button-secondary size-small" v-on:click="Handle_OnClickNext">Next</button>
				</div>
			</section>

			<section class="empty" v-else>
				<ViewUiCalloutIcon
					iconFilename="tv.svg"
					v-bind:message="'No live streams<br />available right now.'"
				/>
			</section>
		</template>
	</ViewFeedItem>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import { Component, Prop, Ref, Vue } from 'vue-property-decorator';
	import { beforeDestroy, bind, event, mounted } from '@/Utility/Decorators';

	/**
	 * Live TV carousel for the feed
	 *
	 * Renders a horizontally scrollable set of live video players using
	 * ChalkyTvVideoTheater. Matches project style: no Vue shorthand and
	 * simple, descriptive comments.
	 *
	 * @author ChalkySticks LLC
	 * @package Feed
	 * @project ChalkySticks Web App
	 */
	@Component
	export default class TVPlayingLive extends ViewBase {
		/**
		 * Visible models, capped by limit
		 *
		 * @return Array<ChalkySticks.Model.Schedule>
		 */
		protected get visibleModels(): Array<ChalkySticks.Model.Schedule> {
			return this.liveCollection.models.slice(0, Math.max(0, this.limit));
		}

		/**
		 * Section title
		 *
		 * @type string
		 */
		@Prop({ default: 'Live Now' })
		public title!: string;

		/**
		 * Max number of live videos to render
		 *
		 * @type number
		 */
		@Prop({ default: 10 })
		public limit!: number;

		/**
		 * Live schedule collection
		 *
		 * @type ChalkySticks.TV.Collection.Live
		 */
		public liveCollection: ChalkySticks.TV.Collection.Live = new ChalkySticks.TV.Collection.Live({
			baseUrl: ChalkySticks.Core.Constants.API_URL_V3,
		});

		/**
		 * Currently focused index in the carousel
		 *
		 * @type number
		 */
		public activeIndex: number = 0;

		/**
		 * Carousel track element
		 *
		 * @type HTMLElement
		 */
		@Ref('track')
		public viewTrack!: HTMLElement;

		/**
		 * Fetch live schedule on create
		 *
		 * @return void
		 */
		@mounted
		public setup(): void {
			this.liveCollection.fetch();
		}

		/**
		 * Go to previous slide
		 *
		 * @return void
		 */
		public previous(): void {
			if (!this.visibleModels.length) return;

			this.activeIndex = (this.activeIndex - 1 + this.visibleModels.length) % this.visibleModels.length;
			this.scrollToActive();
		}

		/**
		 * Go to next slide
		 *
		 * @return void
		 */
		public next(): void {
			if (!this.visibleModels.length) return;

			this.activeIndex = (this.activeIndex + 1) % this.visibleModels.length;
			this.scrollToActive();
		}

		/**
		 * Scroll the track to show the active slide
		 *
		 * @return void
		 */
		protected scrollToActive(): void {
			const slide = (this.$el as HTMLElement).querySelectorAll<HTMLElement>('.slide')[this.activeIndex];

			slide?.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
				inline: 'center',
			});
		}

		// region: Player Events
		// ---------------------------------------------------------------------------

		/**
		 * Player ready handler
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickPrevious(e: PointerEvent): Promise<void> {
			this.previous();
		}

		/**
		 * Player ready handler
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickNext(e: PointerEvent): Promise<void> {
			this.next();
		}

		/**
		 * Player ready handler
		 *
		 * @return Promise<void>
		 */
		protected async Handle_OnPlayerReady(): Promise<void> {
			// bubble if needed later
		}

		/**
		 * Player error handler
		 *
		 * @return Promise<void>
		 */
		protected async Handle_OnPlayerError(): Promise<void> {
			this.next();
		}

		/**
		 * Player ended handler
		 *
		 * @return Promise<void>
		 */
		protected async Handle_OnPlayerEnded(): Promise<void> {
			this.next();
		}

		// endregion: Player Events
	}
</script>

<style lang="scss">
	.view-feed-tvplayinglive {
		.carousel {
			align-items: center;
			display: grid;
			grid-template-columns: auto 1fr auto;
		}

		.track {
			display: grid;
			grid-auto-columns: 100dvw;
			grid-auto-flow: column;
			overflow-x: auto;
			overscroll-behavior-x: contain;
			scroll-snap-type: x mandatory;
		}

		.slide {
			scroll-snap-align: center;
		}

		.empty {
			opacity: 0.8;
			text-align: center;
		}
	}
</style>
